import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Header } from '../../components/Header';
import { PlatformIcon } from '../../components/shared/PlatformIcon';
import { useCreditConfirm } from '../../components/credits/useCreditConfirm';

type Inbox = {
  id: string;
  provider: string;
  platform: string;
  title?: string | null;
  contactName?: string | null;
  contactHandle?: string | null;
  externalAccountId?: string | null;
  metadata?: Record<string, unknown> | null;
};

type ShopeeConnection = {
  id: string;
  shopId: number;
  shopName?: string | null;
  connectionName?: string | null;
  displayName?: string;
  externalId?: string;
};

type MercadoLivreConnection = {
  id: string;
  userId: number;
  nickname?: string | null;
  connectionName?: string | null;
  displayName?: string;
  externalId?: string;
};

type UserOption = { id: string; nome?: string; email: string; ativo?: boolean };
type HumanTransferAgent = { userId: string; name?: string; email?: string; description?: string };

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function crmChatRequest<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api/crm-chat${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...((options.headers as Record<string, string>) || {})
    } as HeadersInit
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.message || payload.error || `HTTP ${response.status}`);
  return payload.data;
}

function providerLabel(provider: string) {
  if (provider === 'waha') return 'WhatsApp Nao Oficial';
  if (provider === 'official') return 'WhatsApp Oficial';
  return provider;
}

function platformLabel(platform: string) {
  const labels: Record<string, string> = {
    whatsapp: 'WhatsApp',
    instagram: 'Instagram',
    facebook: 'Facebook',
    youtube: 'YouTube',
    twitter: 'X/Twitter',
    tiktok: 'TikTok',
    telegram: 'Telegram',
    bluesky: 'Bluesky',
    reddit: 'Reddit',
    mercadolivre: 'Mercado Livre',
    shopee: 'Shopee'
  };
  return labels[platform] || platform;
}

function inboxName(inbox: Inbox) {
  return inbox.title || inbox.contactName || inbox.contactHandle || inbox.externalAccountId || `${platformLabel(inbox.platform)} ${providerLabel(inbox.provider)}`;
}

function shopeeConnectionName(connection?: ShopeeConnection | null) {
  if (!connection) return '';
  return connection.connectionName?.trim() || connection.displayName?.trim() || connection.shopName?.trim() || `Shopee ${connection.shopId}`;
}

function mercadoLivreConnectionName(connection?: MercadoLivreConnection | null) {
  if (!connection) return '';
  return connection.connectionName?.trim() || connection.displayName?.trim() || connection.nickname?.trim() || `Mercado Livre ${connection.userId}`;
}

function isAiEnabled(inbox: Inbox) {
  return inbox.metadata?.aiEnabled === true || inbox.metadata?.aiEnabled === 'true';
}

function savesUserMessages(inbox: Inbox) {
  return inbox.metadata?.saveUserMessagesToAiMemory === true || inbox.metadata?.saveUserMessagesToAiMemory === 'true';
}

function supportsDm(inbox: Inbox) {
  const platform = String(inbox.platform || '').toLowerCase();
  if (inbox.provider === 'marketplace-hub' && platform === 'shopee') return true;
  return ['whatsapp', 'instagram', 'facebook', 'twitter', 'x', 'reddit', 'bluesky', 'telegram'].includes(platform);
}

function supportsComments(inbox: Inbox) {
  const platform = String(inbox.platform || '').toLowerCase();
  if (inbox.provider === 'marketplace-hub') return false;
  return ['instagram', 'facebook', 'youtube', 'linkedin', 'threads', 'reddit', 'bluesky', 'twitter', 'x'].includes(platform);
}

function hasChatIntegration(inbox: Inbox) {
  const platform = String(inbox.platform || '').toLowerCase();
  if (inbox.provider === 'marketplace-hub' && ['mercadolivre', 'shopee'].includes(platform)) return true;
  return supportsDm(inbox) || supportsComments(inbox);
}

type AiChannel = { key: string; label: string; description?: string; metadataKey: string; allowPhoneCapture?: boolean; allowPostContext?: boolean };
type ChannelPromptModal = { open: boolean; inbox: Inbox | null; channel: AiChannel | null; prompt: string; saving: boolean; rewriting: boolean };
type InboundPromptModal = { open: boolean; inbox: Inbox | null; prompt: string; saving: boolean };

function isMarketplaceInbox(inbox: Inbox) {
  return inbox.provider === 'marketplace-hub' && ['mercadolivre', 'shopee'].includes(String(inbox.platform || '').toLowerCase());
}

function aiChannelsForInbox(inbox: Inbox): AiChannel[] {
  const platform = String(inbox.platform || '').toLowerCase();
  if (inbox.provider === 'marketplace-hub' && platform === 'mercadolivre') {
    return [
      { key: 'question', label: 'Responder perguntas Mercado Livre', description: 'Responde perguntas de produto. Apenas texto, uma unica resposta.', metadataKey: 'aiQuestionEnabled' },
      { key: 'sale', label: 'Responder mensagens do comprador Mercado Livre', description: 'Atende automaticamente mensagens pos-venda do comprador. Reclamacoes ficam sempre fora da IA.', metadataKey: 'aiSaleEnabled' }
    ];
  }
  if (inbox.provider === 'marketplace-hub' && platform === 'shopee') {
    return [
      { key: 'dm', label: 'Responder chat Shopee', description: 'Atende automaticamente mensagens de chat da Shopee.', metadataKey: 'aiDmEnabled' },
      { key: 'comment', label: 'Responder comentarios Shopee', description: 'Responde automaticamente avaliacoes/comentarios de produto da Shopee. Apenas texto, uma unica resposta.', metadataKey: 'aiCommentEnabled' }
    ];
  }
  const channels: AiChannel[] = [];
  if (supportsDm(inbox)) channels.push({ key: 'dm', label: 'DM', metadataKey: 'aiDmEnabled', allowPhoneCapture: platform !== 'whatsapp' });
  if (supportsComments(inbox)) channels.push({ key: 'comment', label: 'Comentarios', metadataKey: 'aiCommentEnabled', allowPostContext: true });
  return channels;
}

function channelAiEnabled(inbox: Inbox, channel: string) {
  const config = aiChannelsForInbox(inbox).find((item) => item.key === channel);
  const key = config?.metadataKey || (channel === 'comment' ? 'aiCommentEnabled' : 'aiDmEnabled');
  return inbox.metadata?.[key] === true || inbox.metadata?.[key] === 'true';
}

function channelPromptKey(inbox: Inbox, channel: string) {
  const platform = String(inbox.platform || '').toLowerCase();
  if (inbox.provider === 'marketplace-hub' && platform === 'mercadolivre') {
    if (channel === 'question') return 'mlQuestion';
    if (channel === 'sale') return 'mlSale';
  }
  if (inbox.provider === 'marketplace-hub' && platform === 'shopee') {
    if (channel === 'dm') return 'shopeeDm';
    if (channel === 'comment') return 'shopeeComment';
  }
  if (channel === 'comment') return 'comment';
  if (channel === 'dm') return 'dm';
  return channel;
}

function channelPrompt(inbox: Inbox, channel: string) {
  const prompts = (inbox.metadata?.channelPrompts || {}) as Record<string, string>;
  return String(prompts[channelPromptKey(inbox, channel)] || '');
}

function embedsPostContext(inbox: Inbox) {
  return inbox.metadata?.embedPostContextForComments === true || inbox.metadata?.embedPostContextForComments === 'true';
}

function usesKnowledgeSearch(inbox: Inbox) {
  return inbox.metadata?.knowledgeSearchEnabled !== false && inbox.metadata?.knowledgeSearchEnabled !== 'false';
}

function usesLockedPrompt(inbox: Inbox) {
  return inbox.metadata?.lockedInboundPromptEnabled === true || inbox.metadata?.lockedInboundPromptEnabled === 'true';
}

const DEFAULT_NAME_CAPTURE_MESSAGE = 'Oi! Antes de continuar, qual e o seu nome?';
const DEFAULT_NAME_CAPTURE_MESSAGE_PHONE = 'Oi! Antes de continuar, qual e o seu nome e WhatsApp?';

function nameCaptureEnabled(inbox: Inbox) {
  const v = inbox.metadata?.contactNameCaptureEnabled;
  return v !== false && v !== 'false';
}

function nameCaptureMessage(inbox: Inbox) {
  return typeof inbox.metadata?.contactNameCaptureMessage === 'string' ? inbox.metadata.contactNameCaptureMessage : '';
}

function requiresPhoneCapture(inbox: Inbox) {
  return inbox.metadata?.requirePhoneForDmContactCapture === true || inbox.metadata?.requirePhoneForDmContactCapture === 'true';
}

function humanTransferEnabled(inbox: Inbox) {
  return inbox.metadata?.humanTransferEnabled === true || inbox.metadata?.humanTransferEnabled === 'true';
}

function humanTransferAgents(inbox: Inbox): HumanTransferAgent[] {
  const value = inbox.metadata?.humanTransferAgents;
  return Array.isArray(value) ? value.filter((item): item is HumanTransferAgent => typeof item === 'object' && item !== null) : [];
}

function supportsHumanTransfer(inbox: Inbox, channel: string) {
  const platform = String(inbox.platform || '').toLowerCase();
  if (inbox.provider === 'marketplace-hub' && platform === 'mercadolivre') return channel === 'sale';
  if (inbox.provider === 'marketplace-hub' && platform === 'shopee') return channel === 'dm';
  return channel === 'dm';
}

function Toggle({ checked, disabled, onClick }: { checked: boolean; disabled?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? 'bg-emerald-600' : 'bg-gray-300'} ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:opacity-90'}`}
      aria-pressed={checked}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}

function SettingRow({ title, description, checked, disabled, onToggle }: { title: string; description?: string; checked: boolean; disabled?: boolean; onToggle: () => void }) {
  return (
    <div className="border-t border-gray-100 py-3 first:border-t-0 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-xs font-medium text-gray-900">
          <span>{title}</span>
          <Toggle checked={checked} disabled={disabled} onClick={onToggle} />
        </div>
        {description && <div className="mt-0.5 text-[11px] leading-4 text-gray-500">{description}</div>}
      </div>
    </div>
  );
}

export function AISettingsPage() {
  const [inboxes, setInboxes] = useState<Inbox[]>([]);
  const [shopeeChatSendEnabled, setShopeeChatSendEnabled] = useState(false);
  const [shopeeConnections, setShopeeConnections] = useState<ShopeeConnection[]>([]);
  const [mercadoLivreConnections, setMercadoLivreConnections] = useState<MercadoLivreConnection[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [prompts, setPrompts] = useState<Record<string, string>>({});
  const [delays, setDelays] = useState<Record<string, string>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const { request: requestCreditConfirm, modal: creditConfirmModal } = useCreditConfirm();
  const [promptModal, setPromptModal] = useState<ChannelPromptModal>({ open: false, inbox: null, channel: null, prompt: '', saving: false, rewriting: false });
  const [inboundPromptModal, setInboundPromptModal] = useState<InboundPromptModal>({ open: false, inbox: null, prompt: '', saving: false });
  const [rewritingInboundId, setRewritingInboundId] = useState<string | null>(null);

  const shopeeConnectionByShopId = new Map(shopeeConnections.map((connection) => [String(connection.shopId), connection]));
  const mercadoLivreConnectionByUserId = new Map(mercadoLivreConnections.map((connection) => [String(connection.userId), connection]));

  const visibleAiChannelsForInbox = (inbox: Inbox) => aiChannelsForInbox(inbox).filter((channel) => {
    const isShopeeDm = String(inbox.platform || '').toLowerCase() === 'shopee' && channel.key === 'dm';
    return !isShopeeDm || shopeeChatSendEnabled;
  });

  const displayNameForInbox = (inbox: Inbox) => {
    if (inbox.provider === 'marketplace-hub' && inbox.platform === 'shopee') {
      const connection = shopeeConnectionByShopId.get(String(inbox.externalAccountId || ''));
      return shopeeConnectionName(connection) || inboxName(inbox);
    }
    if (inbox.provider === 'marketplace-hub' && inbox.platform === 'mercadolivre') {
      const connection = mercadoLivreConnectionByUserId.get(String(inbox.externalAccountId || ''));
      return mercadoLivreConnectionName(connection) || inboxName(inbox);
    }
    return inboxName(inbox);
  };

  const loadInboxes = async () => {
    setLoading(true);
    try {
      const [rows, shopeeRows, mercadoLivreRows, capabilities] = await Promise.all([
        crmChatRequest<Inbox[]>('/inboxes'),
        fetch('/api/shopee/connections', { headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit })
          .then((response) => response.ok ? response.json() : { data: [] })
          .then((payload) => payload.success ? (payload.data || []) : [])
          .catch(() => []),
        fetch('/api/mercado-livre/connections', { headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit })
          .then((response) => response.ok ? response.json() : { data: [] })
          .then((payload) => payload.success ? (payload.data || []) : [])
          .catch(() => []),
        crmChatRequest<{ shopeeChatSendEnabled: boolean }>('/capabilities')
          .catch(() => ({ shopeeChatSendEnabled: false }))
      ]);
      setInboxes(rows || []);
      setShopeeConnections(shopeeRows || []);
      setMercadoLivreConnections(mercadoLivreRows || []);
      setShopeeChatSendEnabled(capabilities.shopeeChatSendEnabled !== false);
      setPrompts(Object.fromEntries((rows || []).map((inbox) => [inbox.id, String(inbox.metadata?.inboundPrompt || '')])));
      setDelays(Object.fromEntries((rows || []).map((inbox) => [inbox.id, String(inbox.metadata?.aiResponseDelaySeconds || 30)])));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao carregar caixas de entrada');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch('/api/users?pageSize=100&ativo=true', { headers: { ...authHeaders(), 'Content-Type': 'application/json' } as HeadersInit });
      const payload = await response.json().catch(() => ({}));
      if (response.ok && payload.success) setUsers((payload.data?.users || []).filter((item: UserOption) => item.ativo !== false));
    } catch {
      // lista de humanos fica vazia se a rota nao responder
    }
  };

  useEffect(() => { loadInboxes(); loadUsers(); }, []);

  useEffect(() => {
    const refreshCapabilities = () => {
      crmChatRequest<{ shopeeChatSendEnabled: boolean }>('/capabilities')
        .then((data) => setShopeeChatSendEnabled(data.shopeeChatSendEnabled !== false))
        .catch(() => setShopeeChatSendEnabled(false));
    };
    const interval = window.setInterval(refreshCapabilities, 30000);
    window.addEventListener('focus', refreshCapabilities);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshCapabilities);
    };
  }, []);

  useEffect(() => {
    const isHiddenShopeeDmPrompt = promptModal.open
      && promptModal.inbox?.platform?.toLowerCase() === 'shopee'
      && promptModal.channel?.key === 'dm'
      && !shopeeChatSendEnabled;
    if (isHiddenShopeeDmPrompt) {
      setPromptModal({ open: false, inbox: null, channel: null, prompt: '', saving: false, rewriting: false });
    }
  }, [shopeeChatSendEnabled, promptModal.open, promptModal.inbox, promptModal.channel]);

  const doToggleInbox = async (inbox: Inbox, nextEnabled: boolean) => {
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/ai`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: nextEnabled })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success(isAiEnabled(updated) ? 'IA ativada na caixa' : 'IA desativada na caixa');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar IA');
    } finally {
      setSavingId(null);
    }
  };

  const toggleInbox = async (inbox: Inbox) => {
    const nextEnabled = !isAiEnabled(inbox);
    // Créditos: ativar a IA gera custo por resposta automática. Confirma ao LIGAR.
    if (nextEnabled) {
      requestCreditConfirm({
        title: 'Ativar IA nesta caixa de entrada',
        items: [{ featureKey: 'crm_ai_interaction', qty: 1, label: 'resposta da IA' }],
        note: 'Enquanto ativa, CADA resposta automática da IA às mensagens desta caixa consome este valor. Sem saldo, a IA deixa de responder.',
        confirmLabel: 'Ativar IA',
        onConfirm: () => { doToggleInbox(inbox, true); },
      });
      return;
    }
    doToggleInbox(inbox, false);
  };

  const saveDelay = async (inbox: Inbox) => {
    setSavingId(inbox.id);
    try {
      const seconds = Math.max(1, Math.min(Number(delays[inbox.id] || 30), 300));
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/ai-delay`, {
        method: 'PATCH',
        body: JSON.stringify({ seconds })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      setDelays((current) => ({ ...current, [inbox.id]: String(updated.metadata?.aiResponseDelaySeconds || seconds) }));
      toast.success('Espera da IA salva');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar espera da IA');
    } finally {
      setSavingId(null);
    }
  };

  const toggleSaveUserMessages = async (inbox: Inbox) => {
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/save-user-memory`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !savesUserMessages(inbox) })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success(savesUserMessages(updated) ? 'Mensagens salvas na memoria da IA' : 'Mensagens nao serao salvas na memoria da IA');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar memoria da IA');
    } finally {
      setSavingId(null);
    }
  };

  const toggleKnowledgeSearch = async (inbox: Inbox) => {
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/knowledge-search`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !usesKnowledgeSearch(inbox) })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success(usesKnowledgeSearch(updated) ? 'Busca em base ativada' : 'Busca em base desativada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar busca em base');
    } finally {
      setSavingId(null);
    }
  };

  const toggleLockedPrompt = async (inbox: Inbox) => {
    if (!usesLockedPrompt(inbox)) {
      setInboundPromptModal({ open: true, inbox, prompt: prompts[inbox.id] || String(inbox.metadata?.inboundPrompt || ''), saving: false });
      return;
    }
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/locked-prompt`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !usesLockedPrompt(inbox) })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success(usesLockedPrompt(updated) ? 'Prompt inbound travado' : 'Automacao de funil liberada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar prompt travado');
    } finally {
      setSavingId(null);
    }
  };

  const toggleChannelAi = async (inbox: Inbox, channel: string) => {
    const config = visibleAiChannelsForInbox(inbox).find((item) => item.key === channel) || null;
    if (!config) return;
    const shouldAskPrompt = !channelAiEnabled(inbox, channel) && config && (isMarketplaceInbox(inbox) || channel !== 'dm');
    if (shouldAskPrompt) {
      setPromptModal({ open: true, inbox, channel: config, prompt: channelPrompt(inbox, channel), saving: false, rewriting: false });
      return;
    }
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/channel-ai`, {
        method: 'PATCH',
        body: JSON.stringify({ channel, enabled: !channelAiEnabled(inbox, channel) })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success(channelAiEnabled(updated, channel) ? 'IA ativada no canal' : 'IA desativada no canal');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar IA por canal');
    } finally {
      setSavingId(null);
    }
  };

  const toggleEmbedPostContext = async (inbox: Inbox) => {
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/embed-post-context`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !embedsPostContext(inbox) })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success(embedsPostContext(updated) ? 'Postagem sera enviada ao contexto' : 'Postagem nao sera enviada ao contexto');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar contexto da postagem');
    } finally {
      setSavingId(null);
    }
  };

  const [nameCaptureDrafts, setNameCaptureDrafts] = useState<Record<string, string>>({});

  const saveNameCapture = async (inbox: Inbox, patch: { enabled?: boolean; message?: string }) => {
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/name-capture`, {
        method: 'PATCH',
        body: JSON.stringify(patch)
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      if (patch.message !== undefined) setNameCaptureDrafts((d) => { const next = { ...d }; delete next[inbox.id]; return next; });
      toast.success(patch.message !== undefined ? 'Mensagem salva' : nameCaptureEnabled(updated) ? 'Pergunta de nome ativada' : 'Pergunta de nome desativada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar pergunta de nome');
    } finally {
      setSavingId(null);
    }
  };

  const toggleRequirePhoneCapture = async (inbox: Inbox) => {
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/require-phone-capture`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: !requiresPhoneCapture(inbox) })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success(requiresPhoneCapture(updated) ? 'Captura de WhatsApp ativada' : 'Captura de WhatsApp desativada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar captura de WhatsApp');
    } finally {
      setSavingId(null);
    }
  };

  const saveHumanTransfer = async (inbox: Inbox, enabled: boolean, agents: HumanTransferAgent[]) => {
    setSavingId(inbox.id);
    try {
      const updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/human-transfer`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled, agents })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      toast.success(enabled ? 'Transferencia humana ativada' : 'Transferencia humana desativada');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao atualizar transferencia humana');
    } finally {
      setSavingId(null);
    }
  };

  const toggleHumanTransfer = async (inbox: Inbox) => {
    const enabled = !humanTransferEnabled(inbox);
    const agents = humanTransferAgents(inbox);
    await saveHumanTransfer(inbox, enabled, agents);
  };

  const updateHumanTransferAgent = async (inbox: Inbox, user: UserOption, checked: boolean) => {
    const current = humanTransferAgents(inbox);
    const next = checked
      ? [...current.filter((item) => item.userId !== user.id), { userId: user.id, name: user.nome || user.email, email: user.email, description: '' }]
      : current.filter((item) => item.userId !== user.id);
    await saveHumanTransfer(inbox, humanTransferEnabled(inbox), next);
  };

  const updateHumanTransferDescription = async (inbox: Inbox, user: UserOption, description: string) => {
    const current = humanTransferAgents(inbox);
    const next = current.map((item) => item.userId === user.id ? { ...item, description } : item);
    await saveHumanTransfer(inbox, humanTransferEnabled(inbox), next);
  };

  const rewriteChannelPrompt = async () => {
    const inbox = promptModal.inbox;
    const channel = promptModal.channel;
    if (!inbox || !channel) return;
    setPromptModal((current) => ({ ...current, rewriting: true }));
    try {
      const data = await crmChatRequest<{ prompt: string }>(`/inboxes/${inbox.id}/rewrite-channel-prompt`, {
        method: 'POST',
        body: JSON.stringify({ platform: inbox.platform, channel: channel.key, prompt: promptModal.prompt })
      });
      setPromptModal((current) => ({ ...current, prompt: data.prompt || current.prompt, rewriting: false }));
      toast.success('Prompt reescrito pela IA');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao reescrever prompt');
      setPromptModal((current) => ({ ...current, rewriting: false }));
    }
  };

  const rewriteInboundPrompt = async (inbox: Inbox, inModal = false) => {
    if (rewritingInboundId) return;
    const draft = inModal ? inboundPromptModal.prompt : prompts[inbox.id] || '';
    setRewritingInboundId(inbox.id);
    try {
      const data = await crmChatRequest<{ prompt: string }>(`/inboxes/${inbox.id}/rewrite-channel-prompt`, {
        method: 'POST',
        body: JSON.stringify({ channel: 'dm', target: 'inbound', prompt: draft })
      });
      if (inModal) {
        setInboundPromptModal((current) => current.inbox?.id === inbox.id ? { ...current, prompt: data.prompt || current.prompt } : current);
      } else {
        setPrompts((current) => ({ ...current, [inbox.id]: data.prompt || draft }));
      }
      toast.success('Prompt gerado. Revise o texto antes de salvar.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao gerar prompt');
    } finally {
      setRewritingInboundId(null);
    }
  };

  const saveChannelPromptAndToggle = async (activate: boolean, promptOverride?: string) => {
    const inbox = promptModal.inbox;
    const channel = promptModal.channel;
    if (!inbox || !channel) return;
    setPromptModal((current) => ({ ...current, saving: true }));
    try {
      let updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/channel-prompt`, {
        method: 'PATCH',
        body: JSON.stringify({ channel: channel.key, prompt: promptOverride ?? promptModal.prompt })
      });
      if (activate) {
        updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/channel-ai`, {
          method: 'PATCH',
          body: JSON.stringify({ channel: channel.key, enabled: true })
        });
      }
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      setPromptModal({ open: false, inbox: null, channel: null, prompt: '', saving: false, rewriting: false });
      toast.success(activate ? 'Prompt salvo e IA ativada no canal' : 'Prompt do canal salvo');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar prompt do canal');
      setPromptModal((current) => ({ ...current, saving: false }));
    }
  };

  const saveInboundPromptAndLock = async () => {
    const inbox = inboundPromptModal.inbox;
    if (!inbox) return;
    setInboundPromptModal((current) => ({ ...current, saving: true }));
    try {
      let updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/prompt`, {
        method: 'PATCH',
        body: JSON.stringify({ inboundPrompt: inboundPromptModal.prompt })
      });
      updated = await crmChatRequest<Inbox>(`/inboxes/${inbox.id}/locked-prompt`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: true })
      });
      setInboxes((rows) => rows.map((row) => (row.id === updated.id ? updated : row)));
      setPrompts((current) => ({ ...current, [inbox.id]: inboundPromptModal.prompt }));
      setInboundPromptModal({ open: false, inbox: null, prompt: '', saving: false });
      toast.success(usesLockedPrompt(inbox) ? 'Prompt inbound salvo' : 'Prompt inbound salvo e ativado');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao salvar prompt inbound');
      setInboundPromptModal((current) => ({ ...current, saving: false }));
    }
  };

  return (
    <>
      {creditConfirmModal}
      <Header title="Configuracoes de IA" subtitle="IA por caixa de entrada do CRM Chat, com prompt inbound e handoff interno." />
      <div className="p-6 space-y-4">
        {loading ? (
          <div className="text-sm text-gray-600">Carregando caixas...</div>
        ) : inboxes.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600">Nenhuma caixa de entrada interna encontrada.</div>
        ) : inboxes.filter(hasChatIntegration).length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600">Esse canal nao possui integracao de CHAT</div>
        ) : inboxes.filter(hasChatIntegration).map((inbox) => {
          const enabled = isAiEnabled(inbox);
          const saving = savingId === inbox.id;
          const expanded = expandedId === inbox.id;
          const aiChannels = visibleAiChannelsForInbox(inbox);
          return (
            <section key={inbox.id} className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="rounded-md border border-gray-200 p-1.5"><PlatformIcon platform={inbox.platform} size={18} /></div>
                  <div className="min-w-0">
                    <div className="truncate text-xs font-semibold text-gray-900">{displayNameForInbox(inbox)}</div>
                    <div className="mt-0.5 text-[11px] text-gray-500">{providerLabel(inbox.provider)} - {platformLabel(inbox.platform)}</div>
                    {inbox.provider === 'marketplace-hub' && inbox.platform === 'shopee' && (
                      <div className="mt-0.5 text-[11px] text-gray-500">Loja Shopee: {displayNameForInbox(inbox)}{inbox.externalAccountId ? ` - Shop ID ${inbox.externalAccountId}` : ''}</div>
                    )}
                    {inbox.provider === 'marketplace-hub' && inbox.platform === 'mercadolivre' && (
                      <div className="mt-0.5 text-[11px] text-gray-500">Conta Mercado Livre: {displayNameForInbox(inbox)}{inbox.externalAccountId ? ` - User ID ${inbox.externalAccountId}` : ''}</div>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : inbox.id)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
                  aria-label={expanded ? 'Recolher caixa' : 'Expandir caixa'}
                >
                  <span
                    aria-hidden="true"
                    className={`h-2 w-2 border-b border-r border-gray-500 transition-transform ${expanded ? 'rotate-45' : '-rotate-45'}`}
                  />
                </button>
              </div>

              {expanded && <>
              <div className="border-t border-gray-100 px-4 py-3">
                <div className="space-y-1 rounded-md bg-gray-50 p-3">
                  <SettingRow title="Ativar IA" checked={enabled} disabled={saving} onToggle={() => toggleInbox(inbox)} />

                  {enabled && aiChannels.map((channel) => (
                    <div key={channel.key} className="ml-4 border-l border-gray-200 pl-3">
                      <SettingRow title={channel.label} description={channel.description} checked={channelAiEnabled(inbox, channel.key)} disabled={saving} onToggle={() => toggleChannelAi(inbox, channel.key)} />
                      {channelAiEnabled(inbox, channel.key) && (
                        <button
                          type="button"
                          onClick={() => setPromptModal({ open: true, inbox, channel, prompt: channelPrompt(inbox, channel.key), saving: false, rewriting: false })}
                          className="mb-2 inline-flex h-7 items-center rounded-md border border-gray-300 bg-white px-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Editar prompt
                        </button>
                      )}
                      {channel.key === 'dm' && channelAiEnabled(inbox, channel.key) && (
                        <div className="ml-4 border-l border-gray-200 pl-3">
                          <SettingRow
                            title="Perguntar o nome antes de atender"
                            description="Quando ativo, a IA envia uma mensagem pedindo o nome do lead antes de iniciar o atendimento na DM. Desativado, a IA responde direto e o contato e criado sem nome."
                            checked={nameCaptureEnabled(inbox)}
                            disabled={saving}
                            onToggle={() => saveNameCapture(inbox, { enabled: !nameCaptureEnabled(inbox) })}
                          />
                          {nameCaptureEnabled(inbox) && (() => {
                            const draft = nameCaptureDrafts[inbox.id];
                            const value = draft !== undefined ? draft : nameCaptureMessage(inbox);
                            const placeholder = requiresPhoneCapture(inbox) ? DEFAULT_NAME_CAPTURE_MESSAGE_PHONE : DEFAULT_NAME_CAPTURE_MESSAGE;
                            const dirty = draft !== undefined && draft !== nameCaptureMessage(inbox);
                            return (
                              <div className="mb-3">
                                <label className="mb-1 block text-[11px] font-medium text-gray-700">Mensagem de pergunta do nome</label>
                                <textarea
                                  value={value}
                                  onChange={(e) => setNameCaptureDrafts((d) => ({ ...d, [inbox.id]: e.target.value }))}
                                  placeholder={placeholder}
                                  rows={2}
                                  maxLength={1000}
                                  className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 placeholder:text-gray-400 focus:border-gray-500 focus:outline-none"
                                />
                                <div className="mt-1 flex items-center justify-between gap-2">
                                  <span className="text-[11px] text-gray-500">Vazio = mensagem padrao. Se pedir WhatsApp estiver ativo, inclua o pedido do numero no texto.</span>
                                  <div className="flex gap-2">
                                    {value && (
                                      <button
                                        type="button"
                                        disabled={saving}
                                        onClick={() => saveNameCapture(inbox, { message: '' })}
                                        className="inline-flex h-7 items-center rounded-md border border-gray-300 bg-white px-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                      >
                                        Usar padrao
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      disabled={saving || !dirty}
                                      onClick={() => saveNameCapture(inbox, { message: value })}
                                      className="inline-flex h-7 items-center rounded-md bg-gray-900 px-2.5 text-[11px] font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                    >
                                      Salvar mensagem
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {channel.allowPhoneCapture && channelAiEnabled(inbox, channel.key) && (
                        <div className="ml-4 border-l border-gray-200 pl-3">
                          <SettingRow
                            title="Pedir WhatsApp antes de atender"
                            description="Quando ativo, a IA pede nome e numero de WhatsApp na DM antes de salvar o contato e iniciar o atendimento. Nao se aplica a comentarios."
                            checked={requiresPhoneCapture(inbox)}
                            disabled={saving}
                            onToggle={() => toggleRequirePhoneCapture(inbox)}
                          />
                        </div>
                      )}
                      {supportsHumanTransfer(inbox, channel.key) && channelAiEnabled(inbox, channel.key) && (
                        <div className="ml-4 border-l border-gray-200 pl-3">
                          <SettingRow
                            title="Transferir para humano"
                            description="Quando ativo, a IA ganha uma tool para atribuir a conversa a um agente humano escolhido pelas descricoes abaixo."
                            checked={humanTransferEnabled(inbox)}
                            disabled={saving}
                            onToggle={() => toggleHumanTransfer(inbox)}
                          />
                          {humanTransferEnabled(inbox) && (
                            <div className="mb-3 space-y-2 rounded-md border border-gray-200 bg-white p-3">
                              {users.length === 0 ? (
                                <div className="text-[11px] text-gray-500">Nenhum usuario disponivel neste tenant.</div>
                              ) : users.map((item) => {
                                const selectedAgent = humanTransferAgents(inbox).find((agent) => agent.userId === item.id);
                                const checked = Boolean(selectedAgent);
                                return (
                                  <div key={item.id} className="rounded-md border border-gray-100 bg-gray-50 p-2">
                                    <label className="flex items-center gap-2 text-xs font-medium text-gray-900">
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        disabled={saving}
                                        onChange={(event) => updateHumanTransferAgent(inbox, item, event.target.checked)}
                                        className="h-3.5 w-3.5 rounded border-gray-300"
                                      />
                                      <span className="truncate">{item.nome || item.email}</span>
                                      <span className="truncate text-[11px] font-normal text-gray-500">{item.email}</span>
                                    </label>
                                    {checked && (
                                      <textarea
                                        value={selectedAgent?.description || ''}
                                        onChange={(event) => {
                                          const description = event.target.value;
                                          setInboxes((rows) => rows.map((row) => {
                                            if (row.id !== inbox.id) return row;
                                            const agents = humanTransferAgents(row).map((agent) => agent.userId === item.id ? { ...agent, description } : agent);
                                            return { ...row, metadata: { ...(row.metadata || {}), humanTransferAgents: agents } };
                                          }));
                                        }}
                                        onBlur={(event) => updateHumanTransferDescription(inbox, item, event.target.value)}
                                        rows={2}
                                        className="mt-2 w-full resize-y rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                                        placeholder="Ex: transferir quando o cliente pedir assuntos financeiros, pagamento, negociacao ou boleto."
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                      {!isMarketplaceInbox(inbox) && channel.key === 'dm' && channelAiEnabled(inbox, channel.key) && (
                        <div className="ml-4 border-l border-gray-200 pl-3">
                          <SettingRow
                            title="Usar prompt inbound para contato sem etiqueta"
                            description="Vale apenas para DMs sem etiqueta/funil. Se o contato ja estiver etiquetado, a IA respeita o prompt do funil. Nao se aplica a comentarios."
                            checked={usesLockedPrompt(inbox)}
                            disabled={saving}
                            onToggle={() => toggleLockedPrompt(inbox)}
                          />
                          {usesLockedPrompt(inbox) && (
                            <button
                              type="button"
                              onClick={() => setInboundPromptModal({ open: true, inbox, prompt: String(inbox.metadata?.inboundPrompt || ''), saving: false })}
                              className="mb-2 inline-flex h-7 items-center rounded-md border border-gray-300 bg-white px-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                            >
                              Editar prompt inbound
                            </button>
                          )}
                        </div>
                      )}
                      {channel.allowPostContext && channelAiEnabled(inbox, channel.key) && (
                        <div className="ml-4 border-l border-gray-200 pl-3">
                          <SettingRow
                            title="Embutir postagem no contexto da IA"
                            description="Quando ativo, a legenda/texto da postagem entra no prompt junto com o comentario."
                            checked={embedsPostContext(inbox)}
                            disabled={saving}
                            onToggle={() => toggleEmbedPostContext(inbox)}
                          />
                        </div>
                      )}
                    </div>
                  ))}

                  <SettingRow
                    title="Salvar mensagem do usuario na memoria da IA"
                    description="Mensagens manuais, campanhas e automacoes entram como respostas da IA para manter contexto."
                    checked={savesUserMessages(inbox)}
                    disabled={saving}
                    onToggle={() => toggleSaveUserMessages(inbox)}
                  />

                  <SettingRow
                    title="Ativar busca em base de conhecimento"
                    description="Quando ativo, a IA consulta a base vetorial do tenant antes de responder nesta caixa."
                    checked={usesKnowledgeSearch(inbox)}
                    disabled={saving}
                    onToggle={() => toggleKnowledgeSearch(inbox)}
                  />

                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-[11px] font-medium uppercase tracking-wide text-gray-500">IA Espera para responder</label>
                  <div className="flex max-w-xs items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={300}
                    value={delays[inbox.id] || '30'}
                    onChange={(event) => setDelays((current) => ({ ...current, [inbox.id]: event.target.value }))}
                    className="w-20 rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                  <span className="text-xs text-gray-500">segundos</span>
                  <button
                    type="button"
                    onClick={() => saveDelay(inbox)}
                    disabled={saving}
                    className="inline-flex h-8 items-center rounded-md bg-gray-900 px-3 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-60"
                  >
                    Salvar
                  </button>
                  </div>
                </div>

              </div>
              </>}
            </section>
          );
        })}
      </div>
      {promptModal.open && promptModal.inbox && promptModal.channel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-100 px-4 py-3">
              <div className="text-sm font-semibold text-gray-900">Prompt - {promptModal.channel.label}</div>
              <div className="mt-0.5 text-xs text-gray-500">{displayNameForInbox(promptModal.inbox)}</div>
            </div>
            <div className="px-4 py-3">
              <textarea
                value={promptModal.prompt}
                disabled={promptModal.saving || promptModal.rewriting}
                onChange={(event) => setPromptModal((current) => ({ ...current, prompt: event.target.value }))}
                rows={7}
                className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-xs text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="Descreva a marca, o público e como atender neste canal, ou use Criar com IA."
              />
              {isMarketplaceInbox(promptModal.inbox) ? (
                <div className="mt-2 text-[11px] leading-4 text-gray-500">
                  {promptModal.channel.description || 'Este prompt sera aplicado apenas a este canal.'}
                </div>
              ) : (
                <div className="mt-2 space-y-1.5 rounded-md bg-gray-50 px-2.5 py-2 text-[11px] leading-4 text-gray-600">
                  <div><span className="font-semibold text-gray-800">Este prompt soma aos outros.</span> Ele entra em todas as mensagens deste canal, junto com o prompt do funil (contatos etiquetados) ou com o prompt inbound (contatos sem etiqueta).</div>
                  <div>Use para regras que valem para todo mundo neste canal, como tom de voz, horario de atendimento ou o que nunca prometer.</div>
                  <div>Opcional: se voce ja tem prompt inbound e prompts nos funis, pode deixar em branco.</div>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                onClick={rewriteChannelPrompt}
                disabled={promptModal.saving || promptModal.rewriting}
                className="mr-auto inline-flex h-8 items-center gap-1.5 rounded-md border border-indigo-300 bg-indigo-50 px-3 text-xs font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                title="Usa o contexto da marca e da caixa para criar respostas naturais, respeitando as regras deste canal."
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                  <path d="M12 2l1.6 4.8L18 8.4l-4.4 1.6L12 15l-1.6-4.8L6 8.4l4.4-1.6L12 2z" />
                  <path d="M18.5 13l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6z" />
                </svg>
                {promptModal.rewriting ? 'Gerando...' : promptModal.prompt.trim() ? 'Reescrever com IA' : 'Criar com IA'}
              </button>
              <button
                type="button"
                onClick={() => setPromptModal({ open: false, inbox: null, channel: null, prompt: '', saving: false, rewriting: false })}
                disabled={promptModal.saving || promptModal.rewriting}
                className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => saveChannelPromptAndToggle(!channelAiEnabled(promptModal.inbox!, promptModal.channel!.key))}
                disabled={promptModal.saving || promptModal.rewriting}
                className="inline-flex h-8 items-center rounded-md bg-gray-900 px-3 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {channelAiEnabled(promptModal.inbox, promptModal.channel.key) ? 'Salvar prompt' : 'Salvar e ativar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {inboundPromptModal.open && inboundPromptModal.inbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-100 px-4 py-3">
              <div className="text-sm font-semibold text-gray-900">Prompt inbound para contatos sem etiqueta</div>
              <div className="mt-0.5 text-xs text-gray-500">{displayNameForInbox(inboundPromptModal.inbox)}</div>
            </div>
            <div className="px-4 py-3">
              <textarea
                value={inboundPromptModal.prompt}
                disabled={inboundPromptModal.saving || rewritingInboundId === inboundPromptModal.inbox.id}
                onChange={(event) => setInboundPromptModal((current) => ({ ...current, prompt: event.target.value }))}
                rows={7}
                className="w-full resize-y rounded-md border border-gray-300 px-3 py-2 text-xs text-gray-900 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="Prompt usado apenas em DMs de contatos sem etiqueta/funil. Contatos ja etiquetados seguem o prompt do funil."
              />
              <div className="mt-2 text-[11px] leading-4 text-gray-500">
                Esta configuracao nao substitui o funil de contatos ja etiquetados. Ela so vale para DMs de contatos novos ou sem categoria.
              </div>
              {channelPrompt(inboundPromptModal.inbox, 'dm').trim() && (
                <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-4 text-amber-800">
                  O canal DM desta caixa tambem tem um prompt proprio (botao "Editar prompt" da DM). Ele sera somado a este prompt inbound em todas as respostas.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-gray-100 px-4 py-3">
              <button
                type="button"
                onClick={() => rewriteInboundPrompt(inboundPromptModal.inbox!, true)}
                disabled={inboundPromptModal.saving || rewritingInboundId !== null}
                className="mr-auto inline-flex h-8 items-center rounded-md border border-indigo-300 bg-indigo-50 px-3 text-xs font-medium text-indigo-700 disabled:opacity-60"
              >
                {rewritingInboundId === inboundPromptModal.inbox.id ? 'Gerando...' : inboundPromptModal.prompt.trim() ? 'Reescrever com IA' : 'Criar com IA'}
              </button>
              <button
                type="button"
                onClick={() => setInboundPromptModal({ open: false, inbox: null, prompt: '', saving: false })}
                disabled={inboundPromptModal.saving || rewritingInboundId === inboundPromptModal.inbox.id}
                className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={saveInboundPromptAndLock}
                disabled={inboundPromptModal.saving || rewritingInboundId === inboundPromptModal.inbox.id}
                className="inline-flex h-8 items-center rounded-md bg-gray-900 px-3 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-60"
              >
                {usesLockedPrompt(inboundPromptModal.inbox) ? 'Salvar' : 'Salvar e ativar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
